// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Toast — Global notification system (success, error, warning, info)
// Wrap app with <ToastProvider>, use useToast() hook anywhere
// ================================================================
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

let toastId = 0;

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const ICONS = {
  success: '\u2705',
  error: '\u274C',
  warning: '\u26A0\uFE0F',
  info: '\u2139\uFE0F',
};

const COLORS = {
  success: { bg: '#ECFDF5', border: '#6EE7B7', text: '#065F46' },
  error: { bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B' },
  warning: { bg: '#FFFBEB', border: '#FCD34D', text: '#92400E' },
  info: { bg: '#EFF6FF', border: '#93C5FD', text: '#1E40AF' },
};

export function ToastProvider({ children, position = 'top-right', maxToasts = 5 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const addToast = useCallback((message, options = {}) => {
    const {
      type = 'info',
      duration = 4000,
      action,
      actionLabel,
      dismissible = true,
    } = options;

    const id = ++toastId;

    setToasts((prev) => {
      const next = [...prev, { id, message, type, action, actionLabel, dismissible }];
      return next.slice(-maxToasts);
    });

    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    }

    return id;
  }, [maxToasts, removeToast]);

  const toast = useCallback((message, options) => addToast(message, options), [addToast]);
  toast.success = (msg, opts) => addToast(msg, { ...opts, type: 'success' });
  toast.error = (msg, opts) => addToast(msg, { ...opts, type: 'error', duration: 6000 });
  toast.warning = (msg, opts) => addToast(msg, { ...opts, type: 'warning' });
  toast.info = (msg, opts) => addToast(msg, { ...opts, type: 'info' });

  const positionStyle = {
    'top-right': { top: 16, right: 16 },
    'top-left': { top: 16, left: 16 },
    'bottom-right': { bottom: 16, right: 16 },
    'bottom-left': { bottom: 16, left: 16 },
    'top-center': { top: 16, left: '50%', transform: 'translateX(-50%)' },
    'bottom-center': { bottom: 16, left: '50%', transform: 'translateX(-50%)' },
  }[position] || { top: 16, right: 16 };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {toasts.length > 0 && (
        <div
          className="toast-container"
          style={{ position: 'fixed', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, ...positionStyle }}
        >
          {toasts.map((t) => {
            const colors = COLORS[t.type] || COLORS.info;
            return (
              <div
                key={t.id}
                className="toast-item"
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                  borderRadius: 10, minWidth: 280, maxWidth: 420,
                  background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: 13, fontWeight: 500,
                  animation: 'toast-slide-in 0.25s ease-out',
                }}
              >
                <span style={{ fontSize: 16, flexShrink: 0 }}>{ICONS[t.type]}</span>
                <span style={{ flex: 1 }}>{t.message}</span>
                {t.action && t.actionLabel && (
                  <button
                    onClick={() => { t.action(); removeToast(t.id); }}
                    style={{
                      background: 'none', border: 'none', color: colors.text,
                      fontWeight: 700, cursor: 'pointer', fontSize: 12, textDecoration: 'underline',
                      padding: '2px 4px', fontFamily: 'inherit',
                    }}
                  >
                    {t.actionLabel}
                  </button>
                )}
                {t.dismissible && (
                  <button
                    onClick={() => removeToast(t.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: colors.text,
                      opacity: 0.6, fontSize: 16, padding: '0 2px', lineHeight: 1, fontFamily: 'inherit',
                    }}
                    aria-label="Dismiss"
                  >
                    \u00D7
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
}
